// composables/useUploadArquivos.js
import axios from 'axios';
import { ref } from 'vue';

export function useUploadArquivos() {
    
    const fazendoUpload = ref(false);

    /**
     * Upload global de arquivos
     * @param {Ref<Array>} elementoUpload - Referência reativa do array de arquivos
     * @param {Object} options - Opções adicionais
     * @param {string} options.url - URL da API (padrão: /uploadArquivos)
     * @param {string} options.fieldName - Nome do campo no FormData (padrão: 'files')
     * @param {Function} options.onProgress - Callback de progresso
     * @param {Function} options.onFileComplete - Callback quando um arquivo é completo
     * @returns {Promise<Object>} Resultado do upload
     */
    const uploadArquivos = async (elementoUpload, options = {}) => {

        const {
            url = `${import.meta.env.VITE_API_URL}/uploadArquivos`,
            fieldName = 'files',
            onProgress = null,
            onFileComplete = null
        } = options;

        // Separa arquivos em duas categorias: já enviados e não enviados
        const arquivosJaEnviados = elementoUpload.value.filter(arquivo =>
            arquivo.uniqueId && !arquivo.raw
        );
        
        const arquivosParaUpload = elementoUpload.value.filter(arquivo =>
            !arquivo.uniqueId && arquivo.raw
        );

        // Processa arquivos já enviados primeiro
        if (arquivosJaEnviados.length > 0) {

            for (let i = 0; i < arquivosJaEnviados.length; i++) {

                const arquivo = arquivosJaEnviados[i];
                
                // Encontra o índice no array original
                const indexNoOriginal = elementoUpload.value.findIndex(a => 
                    a.uniqueId === arquivo.uniqueId
                );
                
                if (indexNoOriginal !== -1) {
                    // Mantém os dados existentes no formato desejado
                    elementoUpload.value[indexNoOriginal] = {
                        ...elementoUpload.value[indexNoOriginal],
                        filePath: arquivo.filePath,
                        filename: arquivo.filename,
                        originalName: arquivo.originalName,
                        size: arquivo.size,
                        serverName: arquivo.serverName,
                        uniqueId: arquivo.uniqueId,
                        isExisting: true,
                        displayName: `${arquivo.originalName} (${formatFileSize(arquivo.size)})`
                    };
                }
            }
        }

        // Se não houver arquivos para upload, retorna apenas os já existentes
        if (arquivosParaUpload.length === 0) {

            // Força atualização da reatividade
            elementoUpload.value = [...elementoUpload.value];

            return { 
                status: 'sucesso', 
                total: 0,
                existentes: arquivosJaEnviados.length,
                mensagem: `${arquivosJaEnviados.length} arquivo(s) já existente(s)`
            };
        }

        try {
            
            // Inicia o processo de upload
            fazendoUpload.value = true;

            // Itera sobre os arquivos para upload e envia um por um para a API
            for (let i = 0; i < arquivosParaUpload.length; i++) {

                // Usa o objeto raw como referência para garantir que estamos lidando com o arquivo correto
                const arquivo = arquivosParaUpload[i];
                
                // Callback de progresso
                if (onProgress) {
                    onProgress({
                        atual: i + 1,
                        total: arquivosParaUpload.length,
                        arquivo: arquivo.name,
                        percentual: ((i + 1) / arquivosParaUpload.length) * 100
                    });
                }

                // Cria o FormData para o upload do arquivo atual
                const formData = new FormData();

                // Adiciona o arquivo ao FormData usando o nome do campo definido
                formData.append(fieldName, arquivo.raw, arquivo.name);

                // SÓ FAZ O UPLOAD SE O ARQUIVO NÃO TIVER uniqueId
                const { data: retornoApi } = await axios.post(url, formData, {
                    headers: { 
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                // Verifica se a resposta da API contém os dados do arquivo enviado
                if (retornoApi.files && retornoApi.files[0]) {

                    // Obtém os dados do arquivo enviado a partir da resposta da API
                    const arquivoEnviado = retornoApi.files[0];
                    
                    // Encontra o índice do arquivo original no array usando o objeto raw como referência
                    const indexNoOriginal = elementoUpload.value.findIndex(a => 
                        !a.uniqueId && a.raw === arquivo.raw
                    );

                    // Atualiza o arquivo no array original com os dados retornados pela API
                    if (indexNoOriginal !== -1) {
                        
                        elementoUpload.value[indexNoOriginal] = {
                            ...elementoUpload.value[indexNoOriginal],
                            filePath: arquivoEnviado.filePath,
                            filename: arquivoEnviado.filename,
                            originalName: arquivoEnviado.originalName,
                            size: arquivoEnviado.size,
                            serverName: arquivoEnviado.serverName,
                            uniqueId: arquivoEnviado.uniqueId,
                            isExisting: true,
                            displayName: `${arquivoEnviado.originalName} (${formatFileSize(arquivoEnviado.size)})`
                        };

                        if (onFileComplete) {
                            onFileComplete(arquivoEnviado, i);
                        }
                    }
                }
            }

            // Força atualização da reatividade
            elementoUpload.value = [...elementoUpload.value];
            
            return { 
                status: 'sucesso', 
                total: arquivosParaUpload.length,
                existentes: arquivosJaEnviados.length
            };

        } catch (erro) {
            console.error('Erro no upload:', erro);
            return { status: 'erro', mensagem: erro.message };
        } finally {
            fazendoUpload.value = false;
        }
    };

    // Função auxiliar para formatar tamanho
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return {
        uploadArquivos,
        fazendoUpload,
        formatFileSize
    };
}